import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiScissors, FiStar, FiTrendingUp, FiCheck } = FiIcons;

function Services() {
  const services = [
    {
      icon: FiScissors,
      title: 'Professional Haircuts',
      description: 'Modern and classic haircut styles tailored to your face shape and personal preference.',
      features: ['Consultation', 'Wash & Cut', 'Styling', 'Finishing']
    },
    {
      icon: FiScissors,
      title: 'Beard Trimming & Styling',
      description: 'Expert beard grooming services including trimming, shaping, and styling.',
      features: ['Beard Trimming', 'Mustache Styling', 'Beard Oil Treatment', 'Shaping']
    },
    {
      icon: FiStar,
      title: 'Traditional Turkish Shave',
      description: 'Authentic Turkish hot towel shave experience with premium products.',
      features: ['Hot Towel Treatment', 'Traditional Razor', 'Face Massage', 'Aftercare']
    },
    {
      icon: FiTrendingUp,
      title: 'Hair Styling',
      description: 'Professional hair styling for special occasions and everyday looks.',
      features: ['Event Styling', 'Daily Styling', 'Hair Treatment', 'Product Application']
    },
    {
      icon: FiStar,
      title: 'Facial Treatments',
      description: 'Rejuvenating facial treatments designed specifically for men.',
      features: ['Deep Cleansing', 'Exfoliation', 'Moisturizing', 'Anti-aging Care']
    },
    {
      icon: FiScissors,
      title: 'Eyebrow Shaping',
      description: 'Precise eyebrow trimming and shaping to enhance your facial features.',
      features: ['Trimming', 'Shaping', 'Plucking', 'Styling']
    }
  ];

  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  return (
    <motion.div 
      className="pt-16"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-gray-900 to-gray-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeInUp}>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">Our Services</h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Discover our comprehensive range of premium grooming services, 
              combining traditional Turkish techniques with modern styling.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                className="bg-white p-8 rounded-lg shadow-lg"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5, scale: 1.02 }}
              >
                <SafeIcon icon={service.icon} className="h-12 w-12 text-gray-900 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{service.title}</h3>
                <p className="text-gray-600 mb-6">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center space-x-2">
                      <SafeIcon icon={FiCheck} className="h-4 w-4 text-green-500" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose ADRES?
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              We combine traditional Turkish barbering expertise with modern techniques
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { title: 'Expert Barbers', description: 'Skilled professionals with years of experience' },
              { title: 'Premium Products', description: 'High-quality grooming products and tools' },
              { title: 'Traditional Techniques', description: 'Authentic Turkish barbering methods' },
              { title: 'Modern Comfort', description: 'Contemporary salon environment' }
            ].map((item, index) => (
              <motion.div
                key={index}
                className="text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="bg-gray-900 text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold">{index + 1}</span>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Book Your Appointment Today
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Experience the finest grooming services in Dubai. Call us or visit our salon.
            </p>
            <a 
              href="tel:+971504261841"
              className="bg-white text-gray-900 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              Call +971 50 426 1841
            </a>
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
}

export default Services;